import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Scissors, ArrowLeft, Sparkles, Check } from "lucide-react";

export default function GroomerSignup() {
  const nav = useNavigate();
  const { setSessionUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    business_name: "", name: "", email: "", phone: "", password: "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register/groomer", form);
      localStorage.setItem("pawtrek_token", data.token);
      localStorage.setItem("pawtrek_user", JSON.stringify(data.user));
      setSessionUser(data.user);
      toast.success("Account created — one more step: add your card");
      nav("/billing");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex flex-col">
      <header className="max-w-7xl w-full mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-[#1F1A17]">
          <div className="w-9 h-9 rounded-xl bg-[#C85A17] grid place-content-center text-white">
            <Scissors className="w-4 h-4"/>
          </div>
          <span className="font-display text-xl font-bold">PawTrek</span>
        </Link>
        <Link to="/" className="text-sm text-[#6B625B] flex items-center gap-1"><ArrowLeft className="w-4 h-4"/> Home</Link>
      </header>

      <main className="flex-1 grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto px-6 pb-12 w-full">
        <div className="space-y-4 self-center">
          <div className="pill bg-[#F5E6CF] text-[#A9480F] border border-amber-900/10 w-fit">
            <Sparkles className="w-3 h-3"/> Start free · 7 days
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold leading-[1.05]">
            Run your grooming van<br/>on <span className="text-[#C85A17]">PawTrek</span>.
          </h1>
          <p className="text-[#6B625B] max-w-md">
            Route command, client CRM, in-app messaging, AI reply drafts, Stripe tipping, refunds & CSV exports —
            all under one $29/mo subscription.
          </p>
          <ul className="space-y-2 text-sm text-[#1F1A17] pt-2">
            {["Unlimited clients, pets & appointments","Stripe payments + tips + refunds","AI reply & note assistant",
              "Public before/after gallery pages","Cancel anytime, no long contract"].map((x, i) => (
              <li key={i} className="flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-600"/> {x}
              </li>
            ))}
          </ul>
        </div>

        <form onSubmit={submit} className="bg-white border border-[#E5DCCE] rounded-3xl p-8 shadow-sm self-center space-y-3"
          data-testid="groomer-signup-form">
          <h2 className="font-display text-2xl font-bold">Create your business</h2>
          <p className="text-sm text-[#6B625B]">7 days free · $29/mo after · card required</p>
          <div><Label>Business name</Label>
            <Input required value={form.business_name} onChange={set("business_name")}
              placeholder="e.g. Charlie's Mobile Groom" data-testid="signup-biz"/></div>
          <div><Label>Your name</Label>
            <Input required value={form.name} onChange={set("name")} data-testid="signup-name"/></div>
          <div><Label>Email</Label>
            <Input type="email" required value={form.email} onChange={set("email")} data-testid="signup-email"/></div>
          <div><Label>Phone</Label>
            <Input value={form.phone} onChange={set("phone")} data-testid="signup-phone"/></div>
          <div><Label>Password</Label>
            <Input type="password" required minLength={6} value={form.password}
              onChange={set("password")} data-testid="signup-password"/></div>
          <Button type="submit" disabled={busy}
            className="w-full rounded-full bg-[#C85A17] hover:bg-[#A9480F] btn-tactile h-12 mt-2"
            data-testid="signup-submit">
            {busy ? "Creating account…" : "Start free trial"}
          </Button>
          <p className="text-[10px] text-center text-[#6B625B]">
            After account creation you'll add a card via Stripe. No charge for 7 days.
          </p>
        </form>
      </main>
    </div>
  );
}
